"use client";

import { useMemo, useRef } from "react";
import { rtlLanguages, SrclangType } from "@yedalabs/schema";
import {
    getSceneBackgroundStyle,
    parseBackgroundMediaUrl,
} from "@/features/lomdaEditorV2/lib/sceneBackgroundStyle";
import { isVideoUrl } from "@/features/lomdaEditorV2/lib/mediaAssetUtils";
import { getEffectiveTextColor } from "@/features/lomdaEditorV2/lib/styleUtils";
import { paintZIndex } from "@/features/lomdaEditorV2/lib/elements";
import {
    useEditorMode,
    useMetadata,
    useSelectedScene,
    useUiActions,
} from "@/features/lomdaEditorV2/store";
import { SceneBackgroundVideo } from "@/features/lomdaEditorV2/components/scene/SceneBackgroundVideo";
import { CanvasElement } from "./CanvasElement";
import { SnapGuides } from "./SnapGuides";
import { useCanvasKeyboard } from "./useCanvasKeyboard";
import "@/features/lomdaEditorV2/components/scene/typography.css";

export default function SceneCanvas() {
    const scene = useSelectedScene();
    const metadata = useMetadata();
    const mode = useEditorMode();
    const { clearSelection } = useUiActions();
    const rootRef = useRef<HTMLDivElement>(null);

    useCanvasKeyboard(scene?.id ?? null);

    const sorted = useMemo(
        () =>
            [...(scene?.elements ?? [])].sort(
                (a, b) => paintZIndex(a) - paintZIndex(b)
            ),
        [scene?.elements]
    );

    if (!scene) return null;

    const dir = rtlLanguages.includes(metadata.language as SrclangType)
        ? "rtl"
        : "ltr";
    const bgValue = scene.style?.background || "#FFFFFF";
    const bgMediaUrl = parseBackgroundMediaUrl(bgValue);
    const bgVideoUrl = bgMediaUrl && isVideoUrl(bgMediaUrl) ? bgMediaUrl : null;

    return (
        <div
            ref={rootRef}
            dir={dir}
            data-lomda-scene-canvas
            onPointerDown={() => {
                // Element boxes stopPropagation, so any event reaching here is a
                // genuine background click → deselect.
                if (mode !== "render") clearSelection();
            }}
            className="scene-container relative isolate h-full w-full overflow-hidden"
            style={{
                ...getSceneBackgroundStyle(bgValue),
                color: getEffectiveTextColor(scene, metadata),
            }}
        >
            {bgVideoUrl ? <SceneBackgroundVideo src={bgVideoUrl} /> : null}
            {sorted.map((element) => (
                <CanvasElement
                    key={element.id}
                    scene={scene}
                    element={element}
                    canvasRef={rootRef}
                />
            ))}
            {mode !== "render" && <SnapGuides />}
        </div>
    );
}
